import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'
import apiClient from '@/lib/api-client'
import './i18n/config' // Initialize i18n before rendering
import App from './App'
import { invalidatesQueries } from '@/lib/query-invalidation'
import { ErrorFallback } from '@/components/ErrorFallback'
import { isNative } from '@/platform/native'
import { createMemoryTokenStore, setTokenStore } from '@/lib/token-store'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30 seconds
    },
  },
})

// Invalidate all queries after any mutating API call (POST/PUT/PATCH/DELETE)
// so navigating to another page always shows fresh data
apiClient.interceptors.response.use((response) => {
  if (invalidatesQueries(response.config)) {
    // Invalidate all queries — stale ones will refetch when their component mounts
    queryClient.invalidateQueries();
  }
  return response;
})

// Route chunks are content-hashed and lazily loaded. After a deploy, a tab that
// was opened on the previous build asks for chunk files that no longer exist;
// Vite reports that as `vite:preloadError`. Reload once to pick up the new
// build (the sessionStorage flag stops a reload loop if the failure is real).
window.addEventListener('vite:preloadError', (event) => {
  try {
    if (sessionStorage.getItem('chunk-reload')) return;
    sessionStorage.setItem('chunk-reload', '1');
  } catch {
    return;
  }
  event.preventDefault();
  window.location.reload();
});
// A successful page load clears the guard so a later deploy can trigger it again.
window.addEventListener('load', () => {
  setTimeout(() => {
    try {
      sessionStorage.removeItem('chunk-reload');
    } catch {
      /* storage unavailable */
    }
  }, 10_000);
});

const NATIVE_START_BACKSTOP_MS = 12_000;

function hideSplashScreen() {
  import('@capacitor/splash-screen')
    .then(({ SplashScreen }) => SplashScreen.hide())
    .catch(() => console.warn('Could not hide the splash screen'));
}

const snapshot = document.getElementById('root')!;

// A prerendered marketing page (scripts/prerender-marketing.mjs) arrives with its content inside #root. The app is mounted in a sibling,
// laid out but not painted, and swaps in once it has drawn the same page, so the visitor never sees the snapshot disappear into a blank or
// a spinner. Every other route (and the dev server) has an empty #root and mounts straight into it.
const container = snapshot.hasAttribute('data-prerendered') ? document.createElement('div') : snapshot;
if (container !== snapshot) {
  container.id = 'app-root';
  container.setAttribute('style', 'position:absolute;top:0;left:0;right:0;visibility:hidden;pointer-events:none');
  snapshot.after(container);
  const swap = () => {
    container.removeAttribute('style');
    snapshot.remove();
  };
  const started = performance.now();
  const wait = () => {
    // the real page has a heading and no loading status; give up after 4 s and swap anyway
    const ready = container.querySelector('h1') && !container.querySelector('[role="status"]');
    if (ready || performance.now() - started > 4000) requestAnimationFrame(swap);
    else requestAnimationFrame(wait);
  };
  requestAnimationFrame(wait);
}

// Reuse the root across HMR reloads. React 18's createRoot never sets
// _reactRootContainer (that was the React 17 legacy API), so keying off it
// mounted a second root on every hot reload and stacked two copies of the app.
const root = container.__reactRoot ?? ReactDOM.createRoot(container);
container.__reactRoot = root;

function render() {
  root.render(
    <React.StrictMode>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <App />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

// Development only: VITE_USE_MOCK_API=true answers the quotes/invoices/clients API from memory. The
// condition is a build-time constant, so a normal build drops this branch (and the mock) entirely.
if (import.meta.env.VITE_USE_MOCK_API === 'true') {
  import('./mocks/install').then((m) => {
    m.installMockApi();
    render();
  });
} else if (isNative()) {
  // The app: the native token store (its fresh-install wipe included) is loaded before AuthProvider mounts. A build-time
  // constant, so the website never contains this chunk. Until then, and if the startup fails, tokens stay in memory, never
  // in a cookie.
  setTokenStore(createMemoryTokenStore());
  import('./native/safe-area').then((m) => m.applySafeArea()).catch(() => console.warn('Could not apply the safe area'));
  // startNative has its own timeouts; this is the backstop, so a hung import or plugin call can never keep the app on the splash screen
  // If the backstop wins, startNative is told to stand down: a late finish must not swap the token store or register the outbox
  // under a session that is already running on the memory store.
  const startup = { abandoned: false };
  let backstopTimer: ReturnType<typeof setTimeout>;
  const started = import('./native/bootstrap').then((m) => m.startNative(queryClient, startup));
  const backstop = new Promise<void>((resolve) => {
    backstopTimer = setTimeout(() => {
      startup.abandoned = true;
      resolve();
    }, NATIVE_START_BACKSTOP_MS);
  });
  Promise.race([started, backstop])
    .finally(() => clearTimeout(backstopTimer))
    .catch(() => console.warn('Native startup failed'))
    .then(render)
    // capacitor.config.ts sets launchAutoHide: false, so the splash screen stays up until the app hides it once it has drawn.
    .then(() => requestAnimationFrame(hideSplashScreen));
  setTimeout(hideSplashScreen, NATIVE_START_BACKSTOP_MS + 3000);
} else {
  render();
}

// Extend HTMLElement type to cache the root across HMR
declare global {
  interface HTMLElement {
    __reactRoot?: ReactDOM.Root;
  }
}
